import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningIcon from "@mui/icons-material/Warning";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useNav } from "../context/NavContext";
import { PageHeader } from "./ui";

const PortfolioAlerts = ({
  data,
  previousData,
  onAlertsCountChange,
  scrollPosition,
}) => {
  const NAV_NUMBER = useNav();
  const [expandedStrategies, setExpandedStrategies] = useState(() => {
    const saved = localStorage.getItem("portfolioAlertsExpandedStrategies");
    return saved ? JSON.parse(saved) : {};
  });
  const [crossedThresholdExpanded, setCrossedThresholdExpanded] = useState(
    () => {
      const saved = localStorage.getItem(
        "portfolioAlertsCrossedThresholdExpanded",
      );
      return saved ? JSON.parse(saved) : false;
    },
  );
  const [aboveThresholdExpanded, setAboveThresholdExpanded] = useState(() => {
    const saved = localStorage.getItem("portfolioAlertsAboveThresholdExpanded");
    return saved ? JSON.parse(saved) : false;
  });
  const [crossedThresholdStrategies, setCrossedThresholdStrategies] = useState(
    [],
  );
  const [aboveThresholdStrategies, setAboveThresholdStrategies] = useState([]);
  const [crossed8ThresholdExpanded, setCrossed8ThresholdExpanded] = useState(
    () => {
      const saved = localStorage.getItem(
        "portfolioAlertsCrossed8ThresholdExpanded",
      );
      return saved ? JSON.parse(saved) : false;
    },
  );
  const [above8ThresholdExpanded, setAbove8ThresholdExpanded] = useState(() => {
    const saved = localStorage.getItem(
      "portfolioAlertsAbove8ThresholdExpanded",
    );
    return saved ? JSON.parse(saved) : false;
  });
  const [crossed8ThresholdStrategies, setCrossed8ThresholdStrategies] =
    useState([]);
  const [above8ThresholdStrategies, setAbove8ThresholdStrategies] = useState(
    [],
  );
  const [crossed15ThresholdExpanded, setCrossed15ThresholdExpanded] = useState(
    () => {
      const saved = localStorage.getItem(
        "portfolioAlertsCrossed15ThresholdExpanded",
      );
      return saved ? JSON.parse(saved) : false;
    },
  );
  const [crossed15ThresholdStrategies, setCrossed15ThresholdStrategies] =
    useState([]);
  const [tradeImbalanceExpanded, setTradeImbalanceExpanded] = useState(() => {
    const saved = localStorage.getItem("portfolioAlertsTradeImbalanceExpanded");
    return saved ? JSON.parse(saved) : false;
  });
  const [tradeImbalanceStrategies, setTradeImbalanceStrategies] = useState([]);

  useEffect(() => {
    localStorage.setItem(
      "portfolioAlertsExpandedStrategies",
      JSON.stringify(expandedStrategies),
    );
  }, [expandedStrategies]);

  useEffect(() => {
    localStorage.setItem(
      "portfolioAlertsCrossedThresholdExpanded",
      JSON.stringify(crossedThresholdExpanded),
    );
  }, [crossedThresholdExpanded]);

  useEffect(() => {
    localStorage.setItem(
      "portfolioAlertsAboveThresholdExpanded",
      JSON.stringify(aboveThresholdExpanded),
    );
  }, [aboveThresholdExpanded]);

  useEffect(() => {
    localStorage.setItem(
      "portfolioAlertsCrossed8ThresholdExpanded",
      JSON.stringify(crossed8ThresholdExpanded),
    );
  }, [crossed8ThresholdExpanded]);

  useEffect(() => {
    localStorage.setItem(
      "portfolioAlertsAbove8ThresholdExpanded",
      JSON.stringify(above8ThresholdExpanded),
    );
  }, [above8ThresholdExpanded]);

  useEffect(() => {
    localStorage.setItem(
      "portfolioAlertsCrossed15ThresholdExpanded",
      JSON.stringify(crossed15ThresholdExpanded),
    );
  }, [crossed15ThresholdExpanded]);

  useEffect(() => {
    localStorage.setItem(
      "portfolioAlertsTradeImbalanceExpanded",
      JSON.stringify(tradeImbalanceExpanded),
    );
  }, [tradeImbalanceExpanded]);

  useEffect(() => {
    // Always initialize the arrays, even if data is missing
    if (!data || !previousData) {
      setCrossedThresholdStrategies([]);
      setCrossed8ThresholdStrategies([]);
      setCrossed15ThresholdStrategies([]);
      setTradeImbalanceStrategies([]);
      setAboveThresholdStrategies([]);
      setAbove8ThresholdStrategies([]);

      // Notify parent of zero alerts
      if (onAlertsCountChange) {
        onAlertsCountChange(0);
      }
      return;
    }

    // Calculate strategy totals for current data
    const currentStrategyTotals = data.reduce((acc, position) => {
      const strategy = position.StrategyName;
      if (!acc[strategy]) {
        acc[strategy] = {
          name: strategy,
          totalLmv: 0,
          positions: [],
        };
      }
      acc[strategy].totalLmv += Math.max(0, position.market_value || 0);
      acc[strategy].positions.push(position);
      return acc;
    }, {});

    // Add previous day's data to positions
    Object.values(currentStrategyTotals).forEach((strategy) => {
      strategy.positions.forEach((position) => {
        const prevPosition = previousData.find(
          (p) => p.SYMBOL === position.SYMBOL,
        );
        position.sodLmv = prevPosition
          ? Math.max(0, prevPosition.market_value || 0)
          : 0;
      });
      strategy.positions.sort((a, b) => (b.LMV || 0) - (a.LMV || 0));
    });

    // Calculate strategy totals for previous data
    const previousStrategyTotals = previousData.reduce((acc, position) => {
      const strategy = position.StrategyName;
      if (!acc[strategy]) {
        acc[strategy] = {
          name: strategy,
          totalLmv: 0,
        };
      }
      acc[strategy].totalLmv += Math.max(0, position.market_value || 0);
      return acc;
    }, {});

    // Find strategies that crossed the threshold
    const crossed = Object.entries(currentStrategyTotals)
      .filter(([strategy, data]) => {
        let prevLmv = data.positions.reduce(
          (sum, p) => sum + (p.sodLmv || 0),
          0,
        );
        const currentLmv = data.totalLmv;
        const prevNavPercent = (prevLmv / NAV_NUMBER) * 100;
        const currentNavPercent = (currentLmv / NAV_NUMBER) * 100;
        return prevNavPercent < 4 && currentNavPercent >= 4;
      })
      .map(([strategy, data]) => ({
        ...data,
        previousLmv: previousStrategyTotals[strategy]?.totalLmv || 0,
      }))
      .sort((a, b) => b.totalLmv - a.totalLmv); // Sort crossed strategies by LMV

    // Find strategies above threshold
    const above = Object.entries(currentStrategyTotals)
      .filter(([_, data]) => {
        const currentLmv = data.totalLmv;
        const currentNavPercent = (currentLmv / NAV_NUMBER) * 100;
        return currentNavPercent >= 4;
      })
      .map(([_, data]) => data)
      .sort((a, b) => b.totalLmv - a.totalLmv); // Sort above threshold strategies by LMV

    setCrossedThresholdStrategies(crossed);
    setAboveThresholdStrategies(above);

    // 8% logic
    const crossed8 = Object.entries(currentStrategyTotals)
      .filter(([strategy, data]) => {
        let prevLmv = data.positions.reduce(
          (sum, p) => sum + (p.sodLmv || 0),
          0,
        );
        const currentLmv = data.totalLmv;
        const prevNavPercent = (prevLmv / NAV_NUMBER) * 100;
        const currentNavPercent = (currentLmv / NAV_NUMBER) * 100;
        return prevNavPercent < 8 && currentNavPercent >= 8;
      })
      .map(([strategy, data]) => ({
        ...data,
        previousLmv: previousStrategyTotals[strategy]?.totalLmv || 0,
      }))
      .sort((a, b) => b.totalLmv - a.totalLmv);

    const above8 = Object.entries(currentStrategyTotals)
      .filter(([s, data]) => {
        const currentLmv = data.totalLmv;
        const currentNavPercent = (currentLmv / NAV_NUMBER) * 100;
        return currentNavPercent >= 8;
      })
      .map(([_, data]) => data)
      .sort((a, b) => b.totalLmv - a.totalLmv);

    // 15% logic
    const crossed15 = Object.entries(currentStrategyTotals)
      .filter(([strategy, data]) => {
        let prevLmv = data.positions.reduce(
          (sum, p) => sum + (p.sodLmv || 0),
          0,
        );
        const currentLmv = data.totalLmv;
        const prevNavPercent = (prevLmv / NAV_NUMBER) * 100;
        const currentNavPercent = (currentLmv / NAV_NUMBER) * 100;
        return prevNavPercent < 15 && currentNavPercent >= 15;
      })
      .map(([strategy, data]) => ({
        ...data,
        previousLmv: previousStrategyTotals[strategy]?.totalLmv || 0,
      }))
      .sort((a, b) => b.totalLmv - a.totalLmv);

    setCrossed8ThresholdStrategies(crossed8);
    setAbove8ThresholdStrategies(above8);
    setCrossed15ThresholdStrategies(crossed15);

    const strategyTradeMap = {};
    data.forEach((position) => {
      const strategy = position.StrategyName;
      if (!strategyTradeMap[strategy]) {
        strategyTradeMap[strategy] = {
          name: strategy,
          buyTradeMV: 0,
          sellTradeMV: 0,
          positions: [],
        };
      }
      strategyTradeMap[strategy].buyTradeMV += position.buy_trade_MV || 0;
      strategyTradeMap[strategy].sellTradeMV += position.sell_trade_MV || 0;
      strategyTradeMap[strategy].positions.push(position);
    });
    const tradeImbalance = Object.values(strategyTradeMap)
      .filter((s) => s.buyTradeMV - s.sellTradeMV > 0.015 * NAV_NUMBER)
      .sort(
        (a, b) => b.buyTradeMV - b.sellTradeMV - (a.buyTradeMV - a.sellTradeMV),
      );
    // Attach previous day LMV to positions for table
    tradeImbalance.forEach((strategy) => {
      strategy.positions.forEach((position) => {
        const prevPosition = previousData.find(
          (p) => p.SYMBOL === position.SYMBOL,
        );
        position.sodLmv = prevPosition
          ? Math.max(0, prevPosition.market_value || 0)
          : 0;
      });
      strategy.positions.sort((a, b) => (b.LMV || 0) - (a.LMV || 0));
    });
    setTradeImbalanceStrategies(tradeImbalance);

    // Notify parent of alert count ONLY for strategies crossing thresholds today
    if (onAlertsCountChange) {
      onAlertsCountChange(crossed.length + crossed8.length + crossed15.length);
    }
  }, [data, previousData, onAlertsCountChange, NAV_NUMBER]);

  const handleStrategyExpand = (strategy) => {
    setExpandedStrategies((prev) => ({
      ...prev,
      [strategy]: !prev[strategy],
    }));
  };

  const formatNumber = (value) => {
    if (value == null) return "-";
    const rounded = Math.round(value);
    if (rounded < 0) {
      return `(${Math.abs(rounded).toLocaleString()})`;
    }
    return rounded.toLocaleString();
  };

  const formatNavPercent = (value) => {
    return ((value / NAV_NUMBER) * 100).toFixed(1);
  };

  // Add useEffect to restore scroll position
  useEffect(() => {
    if (scrollPosition > 0) {
      const timer = setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [scrollPosition]);

  return (
    <Box sx={{ width: "100%" }}>
      {/* Page Header */}
      <PageHeader
        title="Portfolio Alerts"
        subtitle="Monitor portfolio thresholds and trading activity"
      />

      {/* Alerts Section */}
      <Paper
        elevation={0}
        sx={{
          p: 1,
          mb: 1,
          backgroundColor: "#fff",
          borderRadius: "16px",
          border: "1px solid rgba(26, 35, 126, 0.2)",
        }}
      >
        {/* Check for alerts condition */}
        {(() => {
          const hasAlerts =
            (crossedThresholdStrategies?.length || 0) > 0 ||
            (crossed8ThresholdStrategies?.length || 0) > 0 ||
            (crossed15ThresholdStrategies?.length || 0) > 0;
          // Note: tradeImbalanceStrategies are NOT included here because they are only shown in Reports section

          if (!hasAlerts) {
            return (
              <Typography
                sx={{
                  textAlign: "center",
                  color: "#666",
                  py: 1,
                  fontSize: "16px",
                  fontWeight: "500",
                }}
              >
                No Alerts
              </Typography>
            );
          }

          return (
            <>
              {/* 4% Threshold Alert */}
              {crossedThresholdStrategies?.length > 0 && (
                <Accordion
                  expanded={crossedThresholdExpanded}
                  onChange={(event, expanded) =>
                    setCrossedThresholdExpanded(expanded)
                  }
                  sx={{
                    mb: 2,
                    "&:before": { display: "none" },
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    borderRadius: "8px !important",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: "rgba(255, 152, 0, 0.08)",
                      borderLeft: "4px solid #ff9800",
                      "&:hover": {
                        backgroundColor: "rgba(255, 152, 0, 0.12)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <WarningIcon sx={{ color: "error.main", mr: 2 }} />
                      <Typography sx={{ fontWeight: 600 }}>
                        Strategies Crossing 4% NAV Threshold Today
                      </Typography>
                      <Chip
                        label={crossedThresholdStrategies?.length}
                        size="small"
                        color="error"
                        sx={{
                          fontWeight: "bold",
                          minWidth: "32px",
                          ml: 1,
                          height: "20px",
                        }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table
                        size="small"
                        sx={{ "& .MuiTableCell-root": { py: 0.5 } }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell width="40px"></TableCell>
                            <TableCell width="200px">
                              <Typography sx={{ fontWeight: 600 }}>
                                Strategy
                              </Typography>
                            </TableCell>
                            <TableCell align="right" width="150px">
                              <Typography sx={{ fontWeight: 600 }}>
                                SOD LMV
                              </Typography>
                            </TableCell>
                            <TableCell align="right" width="100px">
                              <Typography sx={{ fontWeight: 600 }}>
                                Prev % NAV
                              </Typography>
                            </TableCell>
                            <TableCell align="right" width="150px">
                              <Typography sx={{ fontWeight: 600 }}>
                                Current LMV
                              </Typography>
                            </TableCell>
                            <TableCell align="right" width="150px">
                              <Typography sx={{ fontWeight: 600 }}>
                                Net Change
                              </Typography>
                            </TableCell>
                            <TableCell align="right" width="100px">
                              <Typography sx={{ fontWeight: 600 }}>
                                % NAV
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {crossedThresholdStrategies?.map((strategy) => {
                            const totalSodLmv = strategy.positions.reduce(
                              (sum, p) => sum + (p.sodLmv || 0),
                              0,
                            );
                            const totalLmv = strategy.positions.reduce(
                              (sum, p) => sum + (p.LMV || 0),
                              0,
                            );
                            const netChange = totalLmv - totalSodLmv;
                            const prevNavPercent =
                              (totalSodLmv / NAV_NUMBER) * 100;
                            return (
                              <React.Fragment key={strategy.name}>
                                <TableRow
                                  sx={{
                                    "&:last-child td, &:last-child th": {
                                      border: 0,
                                    },
                                    "&:hover": {
                                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                                      cursor: "pointer",
                                    },
                                  }}
                                  onClick={() =>
                                    handleStrategyExpand(strategy.name)
                                  }
                                >
                                  <TableCell>
                                    <IconButton size="small">
                                      {expandedStrategies[strategy.name] ? (
                                        <KeyboardArrowUpIcon />
                                      ) : (
                                        <KeyboardArrowDownIcon />
                                      )}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell>
                                    <Typography sx={{ fontWeight: 500 }}>
                                      {strategy.name}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatNumber(totalSodLmv)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {prevNavPercent.toFixed(1)}%
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatNumber(totalLmv)}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography
                                      sx={{
                                        color:
                                          netChange >= 0
                                            ? "#2e7d32"
                                            : "#d32f2f",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {netChange >= 0 ? "+" : ""}
                                      {formatNumber(netChange)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatNavPercent(totalLmv)}%
                                  </TableCell>
                                </TableRow>
                                {expandedStrategies[strategy.name] && (
                                  <TableRow>
                                    <TableCell colSpan={7} sx={{ p: 0 }}>
                                      <TableContainer>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell width="200px">
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  Security
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="100px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  SOD Qty
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="100px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  Realtime Quantity
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="100px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  Net Trading Quantity
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="120px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  SOD LMV
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="120px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  Current LMV
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="120px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  Net Change
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="100px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  % NAV
                                                </Typography>
                                              </TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {strategy.positions.map(
                                              (position, index) => {
                                                const currentLmv =
                                                  position.LMV || 0;
                                                const sodLmv =
                                                  position.sodLmv || 0;
                                                const netChange =
                                                  currentLmv - sodLmv;
                                                return (
                                                  <TableRow
                                                    key={index}
                                                    sx={{
                                                      backgroundColor:
                                                        "rgba(0, 0, 0, 0.02)",
                                                    }}
                                                  >
                                                    <TableCell>
                                                      {position.security_name}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatNumber(
                                                        position.sod_quantity,
                                                      )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatNumber(
                                                        position.realtime_quantity,
                                                      )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatNumber(
                                                        position.net_trading_quantity,
                                                      )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatNumber(sodLmv)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatNumber(currentLmv)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      <Typography
                                                        sx={{
                                                          color:
                                                            netChange >= 0
                                                              ? "#2e7d32"
                                                              : "#d32f2f",
                                                          fontWeight: 500,
                                                        }}
                                                      >
                                                        {netChange >= 0
                                                          ? "+"
                                                          : ""}
                                                        {formatNumber(
                                                          netChange,
                                                        )}
                                                      </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatNavPercent(
                                                        currentLmv,
                                                      )}
                                                      %
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              },
                                            )}
                                            {/* Strategy Summary Row */}
                                            <TableRow
                                              sx={{
                                                backgroundColor:
                                                  "rgba(26, 35, 126, 0.08)",
                                                "& td": {
                                                  borderTop:
                                                    "2px solid rgba(26, 35, 126, 0.2)",
                                                  fontWeight: 600,
                                                },
                                              }}
                                            >
                                              <TableCell>
                                                Strategy Total
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatNumber(
                                                  strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum +
                                                      (p.sod_quantity || 0),
                                                    0,
                                                  ),
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatNumber(
                                                  strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum +
                                                      (p.realtime_quantity ||
                                                        0),
                                                    0,
                                                  ),
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatNumber(
                                                  strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum +
                                                      (p.net_trading_quantity ||
                                                        0),
                                                    0,
                                                  ),
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatNumber(
                                                  strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum + (p.sodLmv || 0),
                                                    0,
                                                  ),
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatNumber(
                                                  strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum + (p.LMV || 0),
                                                    0,
                                                  ),
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                <Typography
                                                  sx={{
                                                    color:
                                                      strategy.positions.reduce(
                                                        (sum, p) =>
                                                          sum +
                                                          ((p.LMV || 0) -
                                                            (p.sodLmv || 0)),
                                                        0,
                                                      ) >= 0
                                                        ? "#2e7d32"
                                                        : "#d32f2f",
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum +
                                                      ((p.LMV || 0) -
                                                        (p.sodLmv || 0)),
                                                    0,
                                                  ) >= 0
                                                    ? "+"
                                                    : ""}
                                                  {formatNumber(
                                                    strategy.positions.reduce(
                                                      (sum, p) =>
                                                        sum +
                                                        ((p.LMV || 0) -
                                                          (p.sodLmv || 0)),
                                                      0,
                                                    ),
                                                  )}
                                                </Typography>
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatNavPercent(
                                                  strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum + (p.LMV || 0),
                                                    0,
                                                  ),
                                                )}
                                                %
                                              </TableCell>
                                            </TableRow>
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* 8% Threshold Alert */}
              {crossed8ThresholdStrategies.length > 0 && (
                <Accordion
                  expanded={crossed8ThresholdExpanded}
                  onChange={(event, expanded) =>
                    setCrossed8ThresholdExpanded(expanded)
                  }
                  sx={{
                    mb: 2,
                    "&:before": { display: "none" },
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    borderRadius: "8px !important",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: "rgba(255, 152, 0, 0.08)",
                      borderLeft: "4px solid #ff9800",
                      "&:hover": {
                        backgroundColor: "rgba(255, 152, 0, 0.12)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <WarningIcon sx={{ color: "error.main", mr: 2 }} />
                      <Typography sx={{ fontWeight: 600 }}>
                        Strategies Crossing 8% NAV Threshold Today
                      </Typography>
                      <Chip
                        label={crossed8ThresholdStrategies?.length}
                        size="small"
                        color="error"
                        sx={{
                          fontWeight: "bold",
                          minWidth: "32px",
                          ml: 1,
                          height: "20px",
                        }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table
                        size="small"
                        sx={{ "& .MuiTableCell-root": { py: 0.5 } }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell width="40px"></TableCell>
                            <TableCell width="200px">
                              <Typography sx={{ fontWeight: 600 }}>
                                Strategy
                              </Typography>
                            </TableCell>
                            <TableCell align="right" width="150px">
                              <Typography sx={{ fontWeight: 600 }}>
                                SOD LMV
                              </Typography>
                            </TableCell>
                            <TableCell align="right" width="100px">
                              <Typography sx={{ fontWeight: 600 }}>
                                Prev % NAV
                              </Typography>
                            </TableCell>
                            <TableCell align="right" width="150px">
                              <Typography sx={{ fontWeight: 600 }}>
                                Current LMV
                              </Typography>
                            </TableCell>
                            <TableCell align="right" width="150px">
                              <Typography sx={{ fontWeight: 600 }}>
                                Net Change
                              </Typography>
                            </TableCell>
                            <TableCell align="right" width="100px">
                              <Typography sx={{ fontWeight: 600 }}>
                                % NAV
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {crossed8ThresholdStrategies?.map((strategy) => {
                            const totalSodLmv = strategy.positions.reduce(
                              (sum, p) => sum + (p.sodLmv || 0),
                              0,
                            );
                            const totalLmv = strategy.positions.reduce(
                              (sum, p) => sum + (p.LMV || 0),
                              0,
                            );
                            const netChange = totalLmv - totalSodLmv;
                            const prevNavPercent =
                              (totalSodLmv / NAV_NUMBER) * 100;
                            return (
                              <React.Fragment key={strategy.name}>
                                <TableRow
                                  sx={{
                                    "&:last-child td, &:last-child th": {
                                      border: 0,
                                    },
                                    "&:hover": {
                                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                                      cursor: "pointer",
                                    },
                                  }}
                                  onClick={() =>
                                    handleStrategyExpand(strategy.name)
                                  }
                                >
                                  <TableCell>
                                    <IconButton size="small">
                                      {expandedStrategies[strategy.name] ? (
                                        <KeyboardArrowUpIcon />
                                      ) : (
                                        <KeyboardArrowDownIcon />
                                      )}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell>
                                    <Typography sx={{ fontWeight: 500 }}>
                                      {strategy.name}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatNumber(totalSodLmv)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {prevNavPercent.toFixed(1)}%
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatNumber(totalLmv)}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography
                                      sx={{
                                        color:
                                          netChange >= 0
                                            ? "#2e7d32"
                                            : "#d32f2f",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {netChange >= 0 ? "+" : ""}
                                      {formatNumber(netChange)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatNavPercent(totalLmv)}%
                                  </TableCell>
                                </TableRow>
                                {expandedStrategies[strategy.name] && (
                                  <TableRow>
                                    <TableCell colSpan={7} sx={{ p: 0 }}>
                                      <TableContainer>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell width="200px">
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  Security
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="100px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  SOD Qty
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="100px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  Current Qty
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="100px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  Net Trading Quantity
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="120px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  SOD LMV
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="120px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  Current LMV
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="120px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  Net Change
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="100px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  % NAV
                                                </Typography>
                                              </TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {strategy.positions?.map(
                                              (position, index) => {
                                                const currentLmv =
                                                  position.LMV || 0;
                                                const sodLmv =
                                                  position.sodLmv || 0;
                                                const netChange =
                                                  currentLmv - sodLmv;
                                                return (
                                                  <TableRow
                                                    key={index}
                                                    sx={{
                                                      backgroundColor:
                                                        "rgba(0, 0, 0, 0.02)",
                                                    }}
                                                  >
                                                    <TableCell>
                                                      {position.security_name}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatNumber(
                                                        position.sod_quantity,
                                                      )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatNumber(
                                                        position.realtime_quantity,
                                                      )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatNumber(
                                                        position.net_trading_quantity,
                                                      )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatNumber(sodLmv)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatNumber(currentLmv)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      <Typography
                                                        sx={{
                                                          color:
                                                            netChange >= 0
                                                              ? "#2e7d32"
                                                              : "#d32f2f",
                                                          fontWeight: 500,
                                                        }}
                                                      >
                                                        {netChange >= 0
                                                          ? "+"
                                                          : ""}
                                                        {formatNumber(
                                                          netChange,
                                                        )}
                                                      </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatNavPercent(
                                                        currentLmv,
                                                      )}
                                                      %
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              },
                                            )}
                                            {/* Strategy Summary Row */}
                                            <TableRow
                                              sx={{
                                                backgroundColor:
                                                  "rgba(26, 35, 126, 0.08)",
                                                "& td": {
                                                  borderTop:
                                                    "2px solid rgba(26, 35, 126, 0.2)",
                                                  fontWeight: 600,
                                                },
                                              }}
                                            >
                                              <TableCell>
                                                Strategy Total
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatNumber(
                                                  strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum +
                                                      (p.sod_quantity || 0),
                                                    0,
                                                  ),
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatNumber(
                                                  strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum +
                                                      (p.realtime_quantity ||
                                                        0),
                                                    0,
                                                  ),
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatNumber(
                                                  strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum +
                                                      (p.net_trading_quantity ||
                                                        0),
                                                    0,
                                                  ),
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatNumber(
                                                  strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum + (p.sodLmv || 0),
                                                    0,
                                                  ),
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatNumber(
                                                  strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum + (p.LMV || 0),
                                                    0,
                                                  ),
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                <Typography
                                                  sx={{
                                                    color:
                                                      strategy.positions.reduce(
                                                        (sum, p) =>
                                                          sum +
                                                          ((p.LMV || 0) -
                                                            (p.sodLmv || 0)),
                                                        0,
                                                      ) >= 0
                                                        ? "#2e7d32"
                                                        : "#d32f2f",
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum +
                                                      ((p.LMV || 0) -
                                                        (p.sodLmv || 0)),
                                                    0,
                                                  ) >= 0
                                                    ? "+"
                                                    : ""}
                                                  {formatNumber(
                                                    strategy.positions.reduce(
                                                      (sum, p) =>
                                                        sum +
                                                        ((p.LMV || 0) -
                                                          (p.sodLmv || 0)),
                                                      0,
                                                    ),
                                                  )}
                                                </Typography>
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatNavPercent(
                                                  strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum + (p.LMV || 0),
                                                    0,
                                                  ),
                                                )}
                                                %
                                              </TableCell>
                                            </TableRow>
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* 15% Threshold Alert */}
              {crossed15ThresholdStrategies?.length > 0 && (
                <Accordion
                  expanded={crossed15ThresholdExpanded}
                  onChange={(event, expanded) =>
                    setCrossed15ThresholdExpanded(expanded)
                  }
                  sx={{
                    mb: 2,
                    "&:before": { display: "none" },
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    borderRadius: "8px !important",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: "rgba(255, 152, 0, 0.08)",
                      borderLeft: "4px solid #ff9800",
                      "&:hover": {
                        backgroundColor: "rgba(255, 152, 0, 0.12)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <WarningIcon sx={{ color: "error.main", mr: 2 }} />
                      <Typography sx={{ fontWeight: 600 }}>
                        Strategies Crossing 15% NAV Threshold Today
                      </Typography>
                      <Chip
                        label={crossed15ThresholdStrategies?.length}
                        size="small"
                        color="error"
                        sx={{
                          fontWeight: "bold",
                          minWidth: "32px",
                          ml: 1,
                          height: "20px",
                        }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table
                        size="small"
                        sx={{ "& .MuiTableCell-root": { py: 0.5 } }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell width="40px"></TableCell>
                            <TableCell width="200px">
                              <Typography sx={{ fontWeight: 600 }}>
                                Strategy
                              </Typography>
                            </TableCell>
                            <TableCell align="right" width="150px">
                              <Typography sx={{ fontWeight: 600 }}>
                                SOD LMV
                              </Typography>
                            </TableCell>
                            <TableCell align="right" width="100px">
                              <Typography sx={{ fontWeight: 600 }}>
                                Prev % NAV
                              </Typography>
                            </TableCell>
                            <TableCell align="right" width="150px">
                              <Typography sx={{ fontWeight: 600 }}>
                                Current LMV
                              </Typography>
                            </TableCell>
                            <TableCell align="right" width="150px">
                              <Typography sx={{ fontWeight: 600 }}>
                                Net Change
                              </Typography>
                            </TableCell>
                            <TableCell align="right" width="100px">
                              <Typography sx={{ fontWeight: 600 }}>
                                % NAV
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {crossed15ThresholdStrategies?.map((strategy) => {
                            const totalSodLmv = strategy.positions.reduce(
                              (sum, p) => sum + (p.sodLmv || 0),
                              0,
                            );
                            const totalLmv = strategy.positions.reduce(
                              (sum, p) => sum + (p.LMV || 0),
                              0,
                            );
                            const netChange = totalLmv - totalSodLmv;
                            const prevNavPercent =
                              (totalSodLmv / NAV_NUMBER) * 100;
                            return (
                              <React.Fragment key={strategy.name}>
                                <TableRow
                                  sx={{
                                    "&:last-child td, &:last-child th": {
                                      border: 0,
                                    },
                                    "&:hover": {
                                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                                      cursor: "pointer",
                                    },
                                  }}
                                  onClick={() =>
                                    handleStrategyExpand(strategy.name)
                                  }
                                >
                                  <TableCell>
                                    <IconButton size="small">
                                      {expandedStrategies[strategy.name] ? (
                                        <KeyboardArrowUpIcon />
                                      ) : (
                                        <KeyboardArrowDownIcon />
                                      )}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell>
                                    <Typography sx={{ fontWeight: 500 }}>
                                      {strategy.name}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatNumber(totalSodLmv)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {prevNavPercent.toFixed(1)}%
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatNumber(totalLmv)}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography
                                      sx={{
                                        color:
                                          netChange >= 0
                                            ? "#2e7d32"
                                            : "#d32f2f",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {netChange >= 0 ? "+" : ""}
                                      {formatNumber(netChange)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatNavPercent(totalLmv)}%
                                  </TableCell>
                                </TableRow>
                                {expandedStrategies[strategy.name] && (
                                  <TableRow>
                                    <TableCell colSpan={7} sx={{ p: 0 }}>
                                      <TableContainer>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell width="200px">
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  Security
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="100px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  SOD Qty
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="100px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  Current Qty
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="100px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  Net Trading Quantity
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="120px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  SOD LMV
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="120px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  Current LMV
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="120px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  Net Change
                                                </Typography>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                width="100px"
                                              >
                                                <Typography
                                                  sx={{ fontWeight: 600 }}
                                                >
                                                  % NAV
                                                </Typography>
                                              </TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {strategy.positions?.map(
                                              (position, index) => {
                                                const currentLmv =
                                                  position.LMV || 0;
                                                const sodLmv =
                                                  position.sodLmv || 0;
                                                const netChange =
                                                  currentLmv - sodLmv;
                                                return (
                                                  <TableRow
                                                    key={index}
                                                    sx={{
                                                      backgroundColor:
                                                        "rgba(0, 0, 0, 0.02)",
                                                    }}
                                                  >
                                                    <TableCell>
                                                      {position.security_name}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatNumber(
                                                        position.sod_quantity,
                                                      )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatNumber(
                                                        position.realtime_quantity,
                                                      )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatNumber(
                                                        position.net_trading_quantity,
                                                      )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatNumber(sodLmv)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatNumber(currentLmv)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      <Typography
                                                        sx={{
                                                          color:
                                                            netChange >= 0
                                                              ? "#2e7d32"
                                                              : "#d32f2f",
                                                          fontWeight: 500,
                                                        }}
                                                      >
                                                        {netChange >= 0
                                                          ? "+"
                                                          : ""}
                                                        {formatNumber(
                                                          netChange,
                                                        )}
                                                      </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatNavPercent(
                                                        currentLmv,
                                                      )}
                                                      %
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              },
                                            )}
                                            {/* Strategy Summary Row */}
                                            <TableRow
                                              sx={{
                                                backgroundColor:
                                                  "rgba(26, 35, 126, 0.08)",
                                                "& td": {
                                                  borderTop:
                                                    "2px solid rgba(26, 35, 126, 0.2)",
                                                  fontWeight: 600,
                                                },
                                              }}
                                            >
                                              <TableCell>
                                                Strategy Total
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatNumber(
                                                  strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum +
                                                      (p.sod_quantity || 0),
                                                    0,
                                                  ),
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatNumber(
                                                  strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum +
                                                      (p.realtime_quantity ||
                                                        0),
                                                    0,
                                                  ),
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatNumber(
                                                  strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum +
                                                      (p.net_trading_quantity ||
                                                        0),
                                                    0,
                                                  ),
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatNumber(
                                                  strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum + (p.sodLmv || 0),
                                                    0,
                                                  ),
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatNumber(
                                                  strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum + (p.LMV || 0),
                                                    0,
                                                  ),
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                <Typography
                                                  sx={{
                                                    color:
                                                      strategy.positions.reduce(
                                                        (sum, p) =>
                                                          sum +
                                                          ((p.LMV || 0) -
                                                            (p.sodLmv || 0)),
                                                        0,
                                                      ) >= 0
                                                        ? "#2e7d32"
                                                        : "#d32f2f",
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum +
                                                      ((p.LMV || 0) -
                                                        (p.sodLmv || 0)),
                                                    0,
                                                  ) >= 0
                                                    ? "+"
                                                    : ""}
                                                  {formatNumber(
                                                    strategy.positions.reduce(
                                                      (sum, p) =>
                                                        sum +
                                                        ((p.LMV || 0) -
                                                          (p.sodLmv || 0)),
                                                      0,
                                                    ),
                                                  )}
                                                </Typography>
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatNavPercent(
                                                  strategy.positions.reduce(
                                                    (sum, p) =>
                                                      sum + (p.LMV || 0),
                                                    0,
                                                  ),
                                                )}
                                                %
                                              </TableCell>
                                            </TableRow>
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}
            </>
          );
        })()}
      </Paper>

      {/* Reports Section */}
      <Paper
        elevation={0}
        sx={{
          p: 1,
          backgroundColor: "#fff",
          borderRadius: "16px",
          border: "1px solid rgba(26, 35, 126, 0.2)",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: "22px",
            fontWeight: "700",
            mb: 2,
            color: "#1a237e",
            borderBottom: "2px solid rgba(26, 35, 126, 0.1)",
            pb: 1,
          }}
        >
          Reports
        </Typography>

        {/* Strategies Above 4% Report */}
        <Accordion
          expanded={aboveThresholdExpanded}
          onChange={(event, expanded) => setAboveThresholdExpanded(expanded)}
          sx={{
            mb: 2,
            "&:before": { display: "none" },
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            borderRadius: "8px !important",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "rgba(76, 175, 80, 0.08)",
              borderLeft: "4px solid #4caf50",
              "&:hover": {
                backgroundColor: "rgba(76, 175, 80, 0.12)",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <WarningIcon sx={{ color: "success.main", mr: 2 }} />
              <Typography sx={{ fontWeight: 600 }}>
                Strategies Above 4% NAV
              </Typography>
              <Chip
                label={aboveThresholdStrategies.length}
                size="small"
                color="success"
                sx={{
                  fontWeight: "bold",
                  minWidth: "32px",
                  ml: 1,
                  height: "20px",
                }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer>
              <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.5 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell width="40px"></TableCell>
                    <TableCell width="200px">
                      <Typography sx={{ fontWeight: 600 }}>Strategy</Typography>
                    </TableCell>
                    <TableCell align="right" width="150px">
                      <Typography sx={{ fontWeight: 600 }}>SOD LMV</Typography>
                    </TableCell>
                    <TableCell align="right" width="100px">
                      <Typography sx={{ fontWeight: 600 }}>
                        Prev % NAV
                      </Typography>
                    </TableCell>
                    <TableCell align="right" width="150px">
                      <Typography sx={{ fontWeight: 600 }}>
                        Current LMV
                      </Typography>
                    </TableCell>
                    <TableCell align="right" width="150px">
                      <Typography sx={{ fontWeight: 600 }}>
                        Net Change
                      </Typography>
                    </TableCell>
                    <TableCell align="right" width="100px">
                      <Typography sx={{ fontWeight: 600 }}>% NAV</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {aboveThresholdStrategies.map((strategy) => {
                    const totalSodLmv = strategy.positions.reduce(
                      (sum, p) => sum + (p.sodLmv || 0),
                      0,
                    );
                    const totalLmv = strategy.positions.reduce(
                      (sum, p) => sum + (p.LMV || 0),
                      0,
                    );
                    const netChange = totalLmv - totalSodLmv;
                    const prevNavPercent = (totalSodLmv / NAV_NUMBER) * 100;
                    return (
                      <React.Fragment key={strategy.name}>
                        <TableRow
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                              cursor: "pointer",
                            },
                          }}
                          onClick={() => handleStrategyExpand(strategy.name)}
                        >
                          <TableCell>
                            <IconButton size="small">
                              {expandedStrategies[strategy.name] ? (
                                <KeyboardArrowUpIcon />
                              ) : (
                                <KeyboardArrowDownIcon />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontWeight: 500 }}>
                              {strategy.name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(totalSodLmv)}
                          </TableCell>
                          <TableCell align="right">
                            {prevNavPercent.toFixed(1)}%
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(totalLmv)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              sx={{
                                color: netChange >= 0 ? "#2e7d32" : "#d32f2f",
                                fontWeight: 500,
                              }}
                            >
                              {netChange >= 0 ? "+" : ""}
                              {formatNumber(netChange)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {formatNavPercent(totalLmv)}%
                          </TableCell>
                        </TableRow>
                        {expandedStrategies[strategy.name] && (
                          <TableRow>
                            <TableCell colSpan={4} sx={{ p: 0 }}>
                              <TableContainer>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell width="200px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          Security Name
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="120px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          SOD Quantity
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="120px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          Realtime Quantity
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="120px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          Net Trading Quantity
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="120px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          SOD LMV
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="120px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          Current LMV
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="120px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          Net Change
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="100px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          % NAV
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {strategy.positions.map(
                                      (position, index) => {
                                        const currentLmv = position.LMV || 0;
                                        const sodLmv = position.sodLmv || 0;
                                        const netChange = currentLmv - sodLmv;
                                        return (
                                          <TableRow
                                            key={index}
                                            sx={{
                                              backgroundColor:
                                                "rgba(0, 0, 0, 0.02)",
                                            }}
                                          >
                                            <TableCell>
                                              {position.security_name}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatNumber(
                                                position.sod_quantity,
                                              )}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatNumber(
                                                position.realtime_quantity,
                                              )}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatNumber(
                                                position.net_trading_quantity,
                                              )}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatNumber(sodLmv)}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatNumber(currentLmv)}
                                            </TableCell>
                                            <TableCell align="right">
                                              <Typography
                                                sx={{
                                                  color:
                                                    netChange >= 0
                                                      ? "#2e7d32"
                                                      : "#d32f2f",
                                                  fontWeight: 500,
                                                }}
                                              >
                                                {netChange >= 0 ? "+" : ""}
                                                {formatNumber(netChange)}
                                              </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatNavPercent(currentLmv)}%
                                            </TableCell>
                                          </TableRow>
                                        );
                                      },
                                    )}
                                    {/* Strategy Summary Row */}
                                    <TableRow
                                      sx={{
                                        backgroundColor:
                                          "rgba(26, 35, 126, 0.08)",
                                        "& td": {
                                          borderTop:
                                            "2px solid rgba(26, 35, 126, 0.2)",
                                          fontWeight: 600,
                                        },
                                      }}
                                    >
                                      <TableCell>Strategy Total</TableCell>
                                      <TableCell align="right">
                                        {formatNumber(
                                          strategy.positions.reduce(
                                            (sum, p) =>
                                              sum + (p.sod_quantity || 0),
                                            0,
                                          ),
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNumber(
                                          strategy.positions.reduce(
                                            (sum, p) =>
                                              sum + (p.realtime_quantity || 0),
                                            0,
                                          ),
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNumber(
                                          strategy.positions.reduce(
                                            (sum, p) =>
                                              sum +
                                              (p.net_trading_quantity || 0),
                                            0,
                                          ),
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNumber(
                                          strategy.positions.reduce(
                                            (sum, p) => sum + (p.sodLmv || 0),
                                            0,
                                          ),
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNumber(
                                          strategy.positions.reduce(
                                            (sum, p) => sum + (p.LMV || 0),
                                            0,
                                          ),
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography
                                          sx={{
                                            color:
                                              strategy.positions.reduce(
                                                (sum, p) =>
                                                  sum +
                                                  ((p.LMV || 0) -
                                                    (p.sodLmv || 0)),
                                                0,
                                              ) >= 0
                                                ? "#2e7d32"
                                                : "#d32f2f",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {strategy.positions.reduce(
                                            (sum, p) =>
                                              sum +
                                              ((p.LMV || 0) - (p.sodLmv || 0)),
                                            0,
                                          ) >= 0
                                            ? "+"
                                            : ""}
                                          {formatNumber(
                                            strategy.positions.reduce(
                                              (sum, p) =>
                                                sum +
                                                ((p.LMV || 0) -
                                                  (p.sodLmv || 0)),
                                              0,
                                            ),
                                          )}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNavPercent(
                                          strategy.positions.reduce(
                                            (sum, p) => sum + (p.LMV || 0),
                                            0,
                                          ),
                                        )}
                                        %
                                      </TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Strategies Above 8% Report */}
        <Accordion
          expanded={above8ThresholdExpanded}
          onChange={(event, expanded) => setAbove8ThresholdExpanded(expanded)}
          sx={{
            mb: 2,
            "&:before": { display: "none" },
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            borderRadius: "8px !important",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "rgba(76, 175, 80, 0.08)",
              borderLeft: "4px solid #4caf50",
              "&:hover": {
                backgroundColor: "rgba(76, 175, 80, 0.12)",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <WarningIcon sx={{ color: "success.main", mr: 2 }} />
              <Typography sx={{ fontWeight: 600 }}>
                Strategies Above 8% NAV
              </Typography>
              <Chip
                label={above8ThresholdStrategies.length}
                size="small"
                color="success"
                sx={{
                  fontWeight: "bold",
                  minWidth: "32px",
                  ml: 1,
                  height: "20px",
                }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer>
              <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.5 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell width="40px"></TableCell>
                    <TableCell width="200px">
                      <Typography sx={{ fontWeight: 600 }}>Strategy</Typography>
                    </TableCell>
                    <TableCell align="right" width="150px">
                      <Typography sx={{ fontWeight: 600 }}>SOD LMV</Typography>
                    </TableCell>
                    <TableCell align="right" width="100px">
                      <Typography sx={{ fontWeight: 600 }}>
                        Prev % NAV
                      </Typography>
                    </TableCell>
                    <TableCell align="right" width="150px">
                      <Typography sx={{ fontWeight: 600 }}>
                        Current LMV
                      </Typography>
                    </TableCell>
                    <TableCell align="right" width="150px">
                      <Typography sx={{ fontWeight: 600 }}>
                        Net Change
                      </Typography>
                    </TableCell>
                    <TableCell align="right" width="100px">
                      <Typography sx={{ fontWeight: 600 }}>% NAV</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {above8ThresholdStrategies.map((strategy) => {
                    const totalSodLmv = strategy.positions.reduce(
                      (sum, p) => sum + (p.sodLmv || 0),
                      0,
                    );
                    const totalLmv = strategy.positions.reduce(
                      (sum, p) => sum + (p.LMV || 0),
                      0,
                    );
                    const netChange = totalLmv - totalSodLmv;
                    const prevNavPercent = (totalSodLmv / NAV_NUMBER) * 100;
                    return (
                      <React.Fragment key={strategy.name}>
                        <TableRow
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                              cursor: "pointer",
                            },
                          }}
                          onClick={() => handleStrategyExpand(strategy.name)}
                        >
                          <TableCell>
                            <IconButton size="small">
                              {expandedStrategies[strategy.name] ? (
                                <KeyboardArrowUpIcon />
                              ) : (
                                <KeyboardArrowDownIcon />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontWeight: 500 }}>
                              {strategy.name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(totalSodLmv)}
                          </TableCell>
                          <TableCell align="right">
                            {prevNavPercent.toFixed(1)}%
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(totalLmv)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              sx={{
                                color: netChange >= 0 ? "#2e7d32" : "#d32f2f",
                                fontWeight: 500,
                              }}
                            >
                              {netChange >= 0 ? "+" : ""}
                              {formatNumber(netChange)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {formatNavPercent(totalLmv)}%
                          </TableCell>
                        </TableRow>
                        {expandedStrategies[strategy.name] && (
                          <TableRow>
                            <TableCell colSpan={4} sx={{ p: 0 }}>
                              <TableContainer>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell width="200px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          Security Name
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="120px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          SOD Quantity
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="120px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          Realtime Quantity
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="120px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          Net Trading Quantity
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="120px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          SOD LMV
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="120px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          Current LMV
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="120px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          Net Change
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="100px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          % NAV
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {strategy.positions.map(
                                      (position, index) => {
                                        const currentLmv = position.LMV || 0;
                                        const sodLmv = position.sodLmv || 0;
                                        const netChange = currentLmv - sodLmv;
                                        return (
                                          <TableRow
                                            key={index}
                                            sx={{
                                              backgroundColor:
                                                "rgba(0, 0, 0, 0.02)",
                                            }}
                                          >
                                            <TableCell>
                                              {position.security_name}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatNumber(
                                                position.sod_quantity,
                                              )}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatNumber(
                                                position.realtime_quantity,
                                              )}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatNumber(
                                                position.net_trading_quantity,
                                              )}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatNumber(sodLmv)}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatNumber(currentLmv)}
                                            </TableCell>
                                            <TableCell align="right">
                                              <Typography
                                                sx={{
                                                  color:
                                                    netChange >= 0
                                                      ? "#2e7d32"
                                                      : "#d32f2f",
                                                  fontWeight: 500,
                                                }}
                                              >
                                                {netChange >= 0 ? "+" : ""}
                                                {formatNumber(netChange)}
                                              </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatNavPercent(currentLmv)}%
                                            </TableCell>
                                          </TableRow>
                                        );
                                      },
                                    )}
                                    {/* Strategy Summary Row */}
                                    <TableRow
                                      sx={{
                                        backgroundColor:
                                          "rgba(26, 35, 126, 0.08)",
                                        "& td": {
                                          borderTop:
                                            "2px solid rgba(26, 35, 126, 0.2)",
                                          fontWeight: 600,
                                        },
                                      }}
                                    >
                                      <TableCell>Strategy Total</TableCell>
                                      <TableCell align="right">
                                        {formatNumber(
                                          strategy.positions.reduce(
                                            (sum, p) =>
                                              sum + (p.sod_quantity || 0),
                                            0,
                                          ),
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNumber(
                                          strategy.positions.reduce(
                                            (sum, p) =>
                                              sum + (p.realtime_quantity || 0),
                                            0,
                                          ),
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNumber(
                                          strategy.positions.reduce(
                                            (sum, p) =>
                                              sum +
                                              (p.net_trading_quantity || 0),
                                            0,
                                          ),
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNumber(
                                          strategy.positions.reduce(
                                            (sum, p) => sum + (p.sodLmv || 0),
                                            0,
                                          ),
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNumber(
                                          strategy.positions.reduce(
                                            (sum, p) => sum + (p.LMV || 0),
                                            0,
                                          ),
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography
                                          sx={{
                                            color:
                                              strategy.positions.reduce(
                                                (sum, p) =>
                                                  sum +
                                                  ((p.LMV || 0) -
                                                    (p.sodLmv || 0)),
                                                0,
                                              ) >= 0
                                                ? "#2e7d32"
                                                : "#d32f2f",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {strategy.positions.reduce(
                                            (sum, p) =>
                                              sum +
                                              ((p.LMV || 0) - (p.sodLmv || 0)),
                                            0,
                                          ) >= 0
                                            ? "+"
                                            : ""}
                                          {formatNumber(
                                            strategy.positions.reduce(
                                              (sum, p) =>
                                                sum +
                                                ((p.LMV || 0) -
                                                  (p.sodLmv || 0)),
                                              0,
                                            ),
                                          )}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNavPercent(
                                          strategy.positions.reduce(
                                            (sum, p) => sum + (p.LMV || 0),
                                            0,
                                          ),
                                        )}
                                        %
                                      </TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Trade Imbalance Report */}
        <Accordion
          expanded={tradeImbalanceExpanded}
          onChange={(event, expanded) => setTradeImbalanceExpanded(expanded)}
          sx={{
            mb: 2,
            "&:before": { display: "none" },
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            borderRadius: "8px !important",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "rgba(255, 193, 7, 0.08)",
              borderLeft: "4px solid #ffc107",
              "&:hover": {
                backgroundColor: "rgba(255, 193, 7, 0.12)",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <WarningIcon sx={{ color: "warning.main", mr: 2 }} />
              <Typography sx={{ fontWeight: 600 }}>
                Strategies with Trade &gt; 1.5% NAV
              </Typography>
              <Chip
                label={tradeImbalanceStrategies.length}
                size="small"
                color="warning"
                sx={{
                  fontWeight: "bold",
                  minWidth: "32px",
                  ml: 1,
                  height: "20px",
                }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer>
              <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.5 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell width="40px"></TableCell>
                    <TableCell width="200px">
                      <Typography sx={{ fontWeight: 600 }}>Strategy</Typography>
                    </TableCell>
                    <TableCell align="right" width="150px">
                      <Typography sx={{ fontWeight: 600 }}>SOD LMV</Typography>
                    </TableCell>
                    <TableCell align="right" width="100px">
                      <Typography sx={{ fontWeight: 600 }}>
                        Prev % NAV
                      </Typography>
                    </TableCell>
                    <TableCell align="right" width="150px">
                      <Typography sx={{ fontWeight: 600 }}>
                        Current LMV
                      </Typography>
                    </TableCell>
                    <TableCell align="right" width="150px">
                      <Typography sx={{ fontWeight: 600 }}>
                        Net Change
                      </Typography>
                    </TableCell>
                    <TableCell align="right" width="100px">
                      <Typography sx={{ fontWeight: 600 }}>% NAV</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tradeImbalanceStrategies.map((strategy) => {
                    const totalSodLmv = strategy.positions.reduce(
                      (sum, p) => sum + (p.sodLmv || 0),
                      0,
                    );
                    const totalLmv = strategy.positions.reduce(
                      (sum, p) => sum + (p.LMV || 0),
                      0,
                    );
                    const netChange = totalLmv - totalSodLmv;
                    const prevNavPercent = (totalSodLmv / NAV_NUMBER) * 100;
                    return (
                      <React.Fragment key={strategy.name}>
                        <TableRow
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                              cursor: "pointer",
                            },
                          }}
                          onClick={() => handleStrategyExpand(strategy.name)}
                        >
                          <TableCell>
                            <IconButton size="small">
                              {expandedStrategies[strategy.name] ? (
                                <KeyboardArrowUpIcon />
                              ) : (
                                <KeyboardArrowDownIcon />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontWeight: 500 }}>
                              {strategy.name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(totalSodLmv)}
                          </TableCell>
                          <TableCell align="right">
                            {prevNavPercent.toFixed(1)}%
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(totalLmv)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              sx={{
                                color: netChange >= 0 ? "#2e7d32" : "#d32f2f",
                                fontWeight: 500,
                              }}
                            >
                              {netChange >= 0 ? "+" : ""}
                              {formatNumber(netChange)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {formatNavPercent(totalLmv)}%
                          </TableCell>
                        </TableRow>
                        {expandedStrategies[strategy.name] && (
                          <TableRow>
                            <TableCell colSpan={4} sx={{ p: 0 }}>
                              <TableContainer>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell width="200px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          Security Name
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="120px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          SOD Quantity
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="120px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          Realtime Quantity
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="120px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          Net Trading Quantity
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="120px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          SOD LMV
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="120px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          Current LMV
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="120px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          Net Change
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" width="100px">
                                        <Typography sx={{ fontWeight: 600 }}>
                                          % NAV
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {strategy.positions.map(
                                      (position, index) => {
                                        const currentLmv = position.LMV || 0;
                                        const sodLmv = position.sodLmv || 0;
                                        const netChange = currentLmv - sodLmv;
                                        return (
                                          <TableRow
                                            key={index}
                                            sx={{
                                              backgroundColor:
                                                "rgba(0, 0, 0, 0.02)",
                                            }}
                                          >
                                            <TableCell>
                                              {position.security_name}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatNumber(
                                                position.sod_quantity,
                                              )}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatNumber(
                                                position.realtime_quantity,
                                              )}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatNumber(
                                                position.net_trading_quantity,
                                              )}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatNumber(sodLmv)}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatNumber(currentLmv)}
                                            </TableCell>
                                            <TableCell align="right">
                                              <Typography
                                                sx={{
                                                  color:
                                                    netChange >= 0
                                                      ? "#2e7d32"
                                                      : "#d32f2f",
                                                  fontWeight: 500,
                                                }}
                                              >
                                                {netChange >= 0 ? "+" : ""}
                                                {formatNumber(netChange)}
                                              </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatNavPercent(currentLmv)}%
                                            </TableCell>
                                          </TableRow>
                                        );
                                      },
                                    )}
                                    {/* Strategy Summary Row */}
                                    <TableRow
                                      sx={{
                                        backgroundColor:
                                          "rgba(26, 35, 126, 0.08)",
                                        "& td": {
                                          borderTop:
                                            "2px solid rgba(26, 35, 126, 0.2)",
                                          fontWeight: 600,
                                        },
                                      }}
                                    >
                                      <TableCell>Strategy Total</TableCell>
                                      <TableCell align="right">
                                        {formatNumber(
                                          strategy.positions.reduce(
                                            (sum, p) =>
                                              sum + (p.sod_quantity || 0),
                                            0,
                                          ),
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNumber(
                                          strategy.positions.reduce(
                                            (sum, p) =>
                                              sum + (p.realtime_quantity || 0),
                                            0,
                                          ),
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNumber(
                                          strategy.positions.reduce(
                                            (sum, p) =>
                                              sum +
                                              (p.net_trading_quantity || 0),
                                            0,
                                          ),
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNumber(
                                          strategy.positions.reduce(
                                            (sum, p) => sum + (p.sodLmv || 0),
                                            0,
                                          ),
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNumber(
                                          strategy.positions.reduce(
                                            (sum, p) => sum + (p.LMV || 0),
                                            0,
                                          ),
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography
                                          sx={{
                                            color:
                                              strategy.positions.reduce(
                                                (sum, p) =>
                                                  sum +
                                                  ((p.LMV || 0) -
                                                    (p.sodLmv || 0)),
                                                0,
                                              ) >= 0
                                                ? "#2e7d32"
                                                : "#d32f2f",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {strategy.positions.reduce(
                                            (sum, p) =>
                                              sum +
                                              ((p.LMV || 0) - (p.sodLmv || 0)),
                                            0,
                                          ) >= 0
                                            ? "+"
                                            : ""}
                                          {formatNumber(
                                            strategy.positions.reduce(
                                              (sum, p) =>
                                                sum +
                                                ((p.LMV || 0) -
                                                  (p.sodLmv || 0)),
                                              0,
                                            ),
                                          )}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNavPercent(
                                          strategy.positions.reduce(
                                            (sum, p) => sum + (p.LMV || 0),
                                            0,
                                          ),
                                        )}
                                        %
                                      </TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      </Paper>
    </Box>
  );
};

export default PortfolioAlerts;
